import React, { useEffect } from 'react';

import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Button from 'components/Button';
import { Flyout, FlyoutContent, FlyoutTitle } from 'components/Flyout';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';
import { selectIsDesktop } from 'selectors/ui';

import { skuSharedMessages } from '../shared/SkuOrder.messages';

import { useItemReportDetailsQuery } from './SkuItemReports.hooks';
import sharedReportMessages from './SkuItemReports.messages';
import messages from './SkuItemReportsFlyout.messages';
import { TRACKING_SURFACE, useReportsTracking } from './SkuReports.tracking';
import './SkuItemReportsFlyout.scss';

export const SKU_ITEM_REPORTS_FLYOUT_ID = 'sku-item-reports-flyout';

const { block, element } = bem('sku-item-reports-flyout');

const EMPTY_VALUE = '—';

const MoneyValue = ({ money }) =>
  money ? <Message format="currency" value={money.amount} currency={money.currencyCode} /> : EMPTY_VALUE;

MoneyValue.propTypes = {
  money: PropTypes.shape({ amount: PropTypes.number, currencyCode: PropTypes.string }),
};

const StatRow = ({ label, children }) => (
  <div className={element('stat-row')}>
    <span className={element('stat-label')}>{label}</span>
    <span className={element('stat-value')}>{children}</span>
  </div>
);

StatRow.propTypes = {
  label: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

const SkuItemReportsFlyout = ({ item, flyoutId = SKU_ITEM_REPORTS_FLYOUT_ID }) => {
  const intl = useIntl();
  const isDesktop = useSelector(selectIsDesktop);
  const { data: details, isInitialLoading, isError, refetch } = useItemReportDetailsQuery(item?.itemId);

  const tracking = useReportsTracking();
  useEffect(() => {
    if (isError) tracking.errorShown(TRACKING_SURFACE.ITEM_FLYOUT);
  }, [isError]);

  const name = details?.name || item?.name;
  const venueLine = [details?.venueName || item?.venueName, details?.venueSubtitle || item?.venueSubtitle]
    .filter(Boolean)
    .join(' ');

  return (
    <Flyout id={flyoutId} className="sku-item-reports-flyout-shell" showBackButton={!isDesktop}>
      {item && (
        <>
          <FlyoutTitle>
            <span className={element('title')}>
              <span className={element('title-name')}>{name}</span>
              {venueLine && <span className={element('title-venue')}>{venueLine}</span>}
            </span>
          </FlyoutTitle>

          <FlyoutContent>
            <div className={block()}>
              {isInitialLoading && (
                <div className={element('loading')}>
                  <LoadingDots />
                </div>
              )}
              {isError && (
                <div className={element('error')}>
                  <div className={element('error-title')}>
                    <Message message={messages.errorTitle} />
                  </div>
                  <div className={element('error-body')}>
                    <Message message={sharedReportMessages.errorBody} />
                  </div>
                  <Button
                    styled="secondary"
                    onClick={() => {
                      tracking.errorRetried(TRACKING_SURFACE.ITEM_FLYOUT);
                      refetch();
                    }}>
                    <Message message={sharedReportMessages.retry} />
                  </Button>
                </div>
              )}
              {details && !isError && (
                <section className={element('performance')}>
                  <h3 className={element('performance-title')}>
                    <Message message={messages.performanceTitle} />
                  </h3>
                  <div className={element('performance-card')}>
                    <StatRow label={<Message message={messages.unitsSold} />}>
                      {intl.formatNumber(details.unitsSold)}
                    </StatRow>
                    <StatRow label={<Message message={messages.ordersCount} />}>
                      {intl.formatNumber(details.ordersCount)}
                    </StatRow>
                    <StatRow label={<Message message={messages.avgEarningsPerOrder} />}>
                      <MoneyValue money={details.avgEarningsPerOrder} />
                    </StatRow>
                    <div className={element('performance-divider')} />
                    <div className={element('stat-row', { total: true })}>
                      <span className={element('stat-label')}>
                        <Message message={messages.totalEarnings} />
                        <TooltipToggle name="sku-item-reports-flyout-total" className={element('total-info')}>
                          <Isvg src={ICONS.infoGreyLight} />
                        </TooltipToggle>
                        <TooltipContent name="sku-item-reports-flyout-total" place="top">
                          <Message message={skuSharedMessages.payableAmountTooltip} />
                        </TooltipContent>
                      </span>
                      <span className={element('stat-value', { total: true })}>
                        <MoneyValue money={details.totalEarnings} />
                      </span>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </FlyoutContent>
        </>
      )}
    </Flyout>
  );
};

SkuItemReportsFlyout.propTypes = {
  item: PropTypes.shape({
    itemId: PropTypes.string.isRequired,
    name: PropTypes.string,
    venueName: PropTypes.string,
    venueSubtitle: PropTypes.string,
  }),
  flyoutId: PropTypes.string,
};

export default SkuItemReportsFlyout;
